import { useState } from "react"

// import { parseAndThrowAppError } from "../../../errors/recognisedErrors"
import { snackbar } from "../../../utils/general/generalUtils"
import { postClientProtected } from "../clients/clients"
import { DNO_SUBSCRIBE_DEVICE } from "../urls/hybrid"

interface UseDeviceSubscriptionProps {
    deviceSysRef: string
    deviceType?: string
    isSubscribed: boolean
    setIsSubscribed: (isSubscribed: boolean) => void
}

interface UseDeviceSubscriptionResult {
    toggleSubscription: () => Promise<void>
    isLoading: boolean
    error: Error | null
}

/**
 * Hook for managing device subscriptions
 * @param deviceSysRef - The system reference of the device
 * @param deviceType - The type of device
 * @param initialIsSubscribed - Whether the user is initially subscribed
 */
const useDeviceSubscription = ({
    deviceSysRef,
    deviceType,
    isSubscribed,
    setIsSubscribed,
}: UseDeviceSubscriptionProps): UseDeviceSubscriptionResult => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<Error | null>(null)

    const toggleSubscription = async (): Promise<void> => {
        if (!deviceSysRef || !deviceType) {
            setError(new Error("Missing device information"))
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            const url = DNO_SUBSCRIBE_DEVICE(
                deviceSysRef,
                deviceType,
                !isSubscribed
            )
            await postClientProtected(url, {})
            // Toggle the subscription state on success
            setIsSubscribed(!isSubscribed)
            snackbar(
                `Successfully ${
                    isSubscribed ? "unsubscribed" : "subscribed"
                } from device notifications.`,
                "success"
            )
        } catch (err) {
            snackbar(
                "Error occurred while toggling subscription. Please try again.",
                "error"
            )
        } finally {
            setIsLoading(false)
        }
    }

    return {
        toggleSubscription,
        isLoading,
        error,
    }
}

export default useDeviceSubscription
